import { FC, useEffect } from "react";
import { FormEvent, useCallback, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import SearchMdIcon from "@untitled-ui/icons-react/build/esm/SearchMd";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import Input from "@mui/material/Input";
import Stack from "@mui/material/Stack";
import SvgIcon from "@mui/material/SvgIcon";
import Typography from "@mui/material/Typography";

import { MultiSelect } from "src/components/multi-select";
import { useUpdateEffect } from "src/hooks/use-update-effect";
import {
  categoryOptions,
  deviceOptions,
  statusOptions,
} from "src/pages/dashboard/helpers/typehelpers";
import { Device } from "./useGitHubStats";
import { Filters } from "src/sections/dashboard/software/useSoftwareSearch";

interface SearchChip {
  label: string;
  field: "name" | "category" | "status" | "inStock" | "device";
  value: unknown;
  displayValue?: unknown;
}

interface ProductListSearchProps {
  onFiltersChange?: (filters: Filters) => void;
  filters: Filters;
}

export const ProductListSearch: FC<ProductListSearchProps> = (props) => {
  const { onFiltersChange, filters, ...other } = props;
  const queryRef = useRef<HTMLInputElement | null>(null);
  const [chips, setChips] = useState<SearchChip[]>([]);

  useEffect(() => {
    const localData = localStorage.getItem("softwareFilters");
    if (!localData) return;
    const softwareFilters = JSON.parse(localData) as unknown as {
      filters: Filters;
    };
    const newChips = [...chips];

    if (softwareFilters) {
      const filters = softwareFilters["filters"];
      console.log(filters);
      if (filters?.name) {
        newChips.push({
          label: "Name",
          field: "name",
          value: filters.name,
        });
      }

      filters.category.forEach((category) => {
        const option = categoryOptions.find(
          (option) => option.value === category
        );

        newChips.push({
          label: "Category",
          field: "category",
          value: category,
          displayValue: option!.label,
        });
      });

      filters.status.forEach((status) => {
        const option = statusOptions.find((option) => option.value === status);

        newChips.push({
          label: "Status",
          field: "status",
          value: status,
          displayValue: option!.label,
        });
      });

      if (filters.inStock !== undefined) {
        newChips.push({
          label: "Stock",
          field: "inStock",
          value: filters.inStock ? "available" : "outOfStock",
          displayValue: filters.inStock ? "Available" : "Out of Stock",
        });
      }

      filters.devices.forEach((device) => {
        const option = deviceOptions.find((option) => option.value === device);

        newChips.push({
          label: "Device",
          field: "device",
          value: device,
          displayValue: option!.label,
        });
      });
    }

    setChips(newChips);
  }, []);

  const handleChipsUpdate = useCallback(() => {
    const newFilter: Filters = {
      name: undefined,
      category: [],
      status: [],
      inStock: undefined,
      devices: [],
    };
    chips.forEach((chip) => {
      switch (chip.field) {
        case "name":
          // There will (or should) be only one chips with field "name"
          // so we can set up it directly
          newFilter.name = chip.value as string;
          break;
        case "category":
          newFilter.category.push(chip.value as string);
          break;
        case "status":
          newFilter.status.push(chip.value as string);
          break;
        case "inStock":
          // The value can be "available" or "outOfStock" and we transform it to a boolean
          newFilter.inStock = chip.value === "available";
          break;
        case "device":
          newFilter.devices.push(chip.value as Device);
          break;
        default:
          break;
      }
    });

    onFiltersChange?.(newFilter);
  }, [chips, onFiltersChange]);

  useUpdateEffect(() => {
    handleChipsUpdate();
  }, [chips, handleChipsUpdate]);

  const handleChipDelete = useCallback((deletedChip: SearchChip): void => {
    setChips((prevChips) => {
      return prevChips.filter((chip) => {
        // There can exist multiple chips for the same field.
        // Filter them by value.

        return !(
          deletedChip.field === chip.field && deletedChip.value === chip.value
        );
      });
    });
  }, []);

  const handleQueryChange = useCallback(
    (event: FormEvent<HTMLFormElement>): void => {
      event.preventDefault();

      const value = queryRef.current?.value || "";

      setChips((prevChips) => {
        const found = prevChips.find((chip) => chip.field === "name");

        if (found && value) {
          return prevChips.map((chip) => {
            if (chip.field === "name") {
              return {
                ...chip,
                value: queryRef.current?.value || "",
              };
            }

            return chip;
          });
        }

        if (found && !value) {
          return prevChips.filter((chip) => chip.field !== "name");
        }

        if (!found && value) {
          const chip: SearchChip = {
            label: "Name",
            field: "name",
            value,
          };

          return [...prevChips, chip];
        }

        return prevChips;
      });

      if (queryRef.current) {
        queryRef.current.value = "";
      }
    },
    []
  );

  const handleCategoryChange = useCallback((values: string[]): void => {
    setChips((prevChips) => {
      const valuesFound: string[] = [];

      // First cleanup the previous chips
      const newChips = prevChips.filter((chip) => {
        if (chip.field !== "category") {
          return true;
        }

        const found = values.includes(chip.value as string);

        if (found) {
          valuesFound.push(chip.value as string);
        }

        return found;
      });

      // Nothing changed
      if (values.length === valuesFound.length) {
        return newChips;
      }

      values.forEach((value) => {
        if (!valuesFound.includes(value)) {
          const option = categoryOptions.find(
            (option) => option.value === value
          );

          newChips.push({
            label: "Category",
            field: "category",
            value,
            displayValue: option!.label,
          });
        }
      });

      return newChips;
    });
  }, []);

  const handleStatusChange = useCallback((values: string[]): void => {
    setChips((prevChips) => {
      const valuesFound: string[] = [];

      // First cleanup the previous chips
      const newChips = prevChips.filter((chip) => {
        if (chip.field !== "status") {
          return true;
        }

        const found = values.includes(chip.value as string);

        if (found) {
          valuesFound.push(chip.value as string);
        }

        return found;
      });

      // Nothing changed
      if (values.length === valuesFound.length) {
        return newChips;
      }

      values.forEach((value) => {
        if (!valuesFound.includes(value)) {
          const option = statusOptions.find((option) => option.value === value);

          newChips.push({
            label: "Status",
            field: "status",
            value,
            displayValue: option!.label,
          });
        }
      });

      return newChips;
    });
  }, []);

  const handleDeviceChange = useCallback((values: string[]): void => {
    setChips((prevChips) => {
      const valuesFound: string[] = [];

      // First cleanup the previous chips
      const newChips = prevChips.filter((chip) => {
        if (chip.field !== "device") {
          return true;
        }

        const found = values.includes(chip.value as string);

        if (found) {
          valuesFound.push(chip.value as string);
        }

        return found;
      });

      // Nothing changed
      if (values.length === valuesFound.length) {
        return newChips;
      }

      values.forEach((value) => {
        if (!valuesFound.includes(value)) {
          const option = deviceOptions.find((option) => option.value === value);

          newChips.push({
            label: "Device",
            field: "device",
            value,
            displayValue: option!.label,
          });
        }
      });

      return newChips;
    });
  }, []);

  const handleStockChange = useCallback((values: string[]): void => {
    // Stock can only have one value, even if displayed as multi-select, so we select the first one.
    // This example allows you to select one value or "All", which is not included in the
    // rest of multi-selects.

    setChips((prevChips) => {
      // First cleanup the previous chips
      const newChips = prevChips.filter((chip) => chip.field !== "inStock");
      const latestValue = values[values.length - 1];

      switch (latestValue) {
        case "available":
          newChips.push({
            label: "Stock",
            field: "inStock",
            value: "available",
            displayValue: "Available",
          });
          break;
        case "outOfStock":
          newChips.push({
            label: "Stock",
            field: "inStock",
            value: "outOfStock",
            displayValue: "Out of Stock",
          });
          break;
        default:
          // Should be "all", so we do not add this filter
          break;
      }

      return newChips;
    });
  }, []);

  // We memoize this part to prevent re-render issues
  const categoryValues = useMemo(
    () =>
      chips
        .filter((chip) => chip.field === "category")
        .map((chip) => chip.value) as string[],
    [chips]
  );

  const statusValues = useMemo(
    () =>
      chips
        .filter((chip) => chip.field === "status")
        .map((chip) => chip.value) as string[],
    [chips]
  );

  const deviceValues = useMemo(
    () =>
      chips
        .filter((chip) => chip.field === "device")
        .map((chip) => chip.value) as string[],
    [chips]
  );

  const stockValues = useMemo(() => {
    const values = chips
      .filter((chip) => chip.field === "inStock")
      .map((chip) => chip.value) as string[];

    // Since we do not display the "all" as chip, we add it to the multi-select as a selected value
    if (values.length === 0) {
      values.unshift("all");
    }

    return values;
  }, [chips]);

  const showChips = chips.length > 0;

  return (
    <div {...other}>
      {/* <Stack
        alignItems="center"
        component="form"
        direction="row"
        onSubmit={handleQueryChange}
        spacing={2}
        sx={{ p: 2 }}
      >
        <SvgIcon>
          <SearchMdIcon />
        </SvgIcon>
        <Input
          defaultValue=""
          disableUnderline
          fullWidth
          inputProps={{ ref: queryRef }}
          placeholder="Search"
          sx={{ flexGrow: 1 }}
        />
      </Stack> */}
      <Divider />
      {showChips ? (
        <Stack
          alignItems="center"
          direction="row"
          flexWrap="wrap"
          gap={1}
          sx={{ p: 2 }}
        >
          {chips.map((chip, index) => (
            <Chip
              key={index}
              label={
                <Box
                  sx={{
                    alignItems: "center",
                    display: "flex",
                    "& span": {
                      fontWeight: 600,
                    },
                  }}
                >
                  <>
                    <span>{chip.label}</span>: {chip.displayValue || chip.value}
                  </>
                </Box>
              }
              onDelete={(): void => handleChipDelete(chip)}
              variant="outlined"
            />
          ))}
        </Stack>
      ) : (
        <Box sx={{ p: 2.5 }}>
          <Typography color="text.secondary" variant="subtitle2">
            No filters applied
          </Typography>
        </Box>
      )}
      <Divider />
      <Stack
        alignItems="center"
        direction="row"
        flexWrap="wrap"
        spacing={1}
        sx={{ p: 1 }}
      >
        {/* <MultiSelect
          label="Category"
          onChange={handleCategoryChange}
          options={categoryOptions}
          value={categoryValues}
        />
        <MultiSelect
          label="Status"
          onChange={handleStatusChange}
          options={statusOptions}
          value={statusValues}
        /> */}
        <MultiSelect
          label="Device"
          onChange={handleDeviceChange}
          options={deviceOptions}
          value={deviceValues}
        />
      </Stack>
    </div>
  );
};

ProductListSearch.propTypes = {
  onFiltersChange: PropTypes.func,
};
